import './styles/main.css';
import './styles/login.css';
import './styles/ranking.css';
import './styles/inline-ranking.css';
import './styles/mode-selection.css';
import './styles/multiplayer.css';
import { HexBoard } from './game/hex/HexBoard';
import { HexRenderer } from './ui/HexRenderer';
import { MiniGameMode } from './game/MiniGameMode';
import { MiniGameControls } from './ui/MiniGameControls';
import { RouteSearcherV4 } from './game/RouteSearcherV4';
import { AuthService } from './services/AuthService';
import { LoginScreen } from './components/LoginScreen';
import { UserProfileDisplay } from './components/UserProfileDisplay';
import { RankingDisplay } from './components/RankingDisplay';
import { InlineRanking } from './components/InlineRanking';
import { GameResultService } from './services/GameResultService';
import { User } from './models/User';
import { ModeSelection } from './components/ModeSelection';
import { MultiplayerLobby } from './components/MultiplayerLobby';
import { MultiplayerRoom } from './components/MultiplayerRoom';
import { MultiplayerService } from './services/MultiplayerService';
import { TwitterShare } from './utils/TwitterShare';
import { RuleModalScreen } from './components/RuleModalScreen';

class ChimdonMini {
  private board: HexBoard;
  private renderer: HexRenderer;
  private miniGame: MiniGameMode;
  private controls: MiniGameControls;
  private canvas: HTMLCanvasElement;
  private authService: AuthService;
  private loginScreen: LoginScreen | null = null;
  private profileDisplay: UserProfileDisplay | null = null;
  private rankingDisplay: RankingDisplay | null = null;
  private inlineRanking: InlineRanking | null = null;
  private gameResultService: GameResultService;
  private multiplayerService: MultiplayerService;
  private currentUser: User | null = null;
  private gameMode: 'single' | 'multi' = 'single';
  private currentView: 'title' | 'mode-selection' | 'game' | 'lobby' | 'room' = 'title';
  private modeSelection: ModeSelection | null = null;
  private multiplayerLobby: MultiplayerLobby | null = null;
  private multiplayerRoom: MultiplayerRoom | null = null;
  private currentRoomId: string | null = null;
  private roomUnsubscribe: (() => void) | null = null;
  private ruleModal: RuleModalScreen | null = null;

  constructor() {
    this.canvas = document.getElementById('game-canvas') as HTMLCanvasElement;
    
    if (!this.canvas) {
      throw new Error('Canvas element not found');
    }

    // Initialize services
    this.authService = new AuthService();
    this.gameResultService = new GameResultService();
    this.multiplayerService = new MultiplayerService(this.authService);

    // 初期画面用の仮配置（ゲーム開始前の表示用）
    this.board = new HexBoard({
      BLUE_1: 4,
      RED_1: 4,
      BLUE_2: 4,
      RED_2: 2,
      BLUE_1_RED_1: 6,
      BLUE_2_RED_1: 4,
      BLUE_1_RED_2: 2,
      BLUE_1_STAR: 0,
      RED_1_STAR: 0,
      BLUE_2_STAR: 0,
      RED_2_STAR: 0,
      BLUE_1_RED_1_STAR: 0,
      BLUE_2_RED_1_STAR: 0,
      BLUE_1_RED_2_STAR: 0
    });
    
    this.renderer = new HexRenderer(this.canvas);
    this.miniGame = new MiniGameMode(this.board);
    this.controls = new MiniGameControls(this.board, this.miniGame);
    
    // ゲーム完了時のコールバックを設定
    this.miniGame.setOnGameComplete((totalTime, roundNumber, boardSeed, hintPenalty) => {
      this.handleGameComplete(totalTime, roundNumber, boardSeed, hintPenalty);
    });
    
    // タイトル画面を表示
    this.showTitleScreen();
    
    // Check if user is already logged in
    setTimeout(() => {
      const existingUser = this.authService.getCurrentUser();
      if (existingUser) {
        this.currentUser = existingUser;
        console.log('既存のログインユーザーを検出:', existingUser.displayName);
      }
    }, 100); // AuthServiceの初期化を待つ
    
    // カスタムイベントリスナー設定
    window.addEventListener('show-login', () => {
      this.showLoginScreen();
    });
    
    this.init();
  }

  private init(): void {
    this.setupCanvasEvents();
    this.setupRuleButton();

    // デバッグ情報の出力
    console.log('チムドンミニ - ミニゲーム版');
    console.log('緑チップなしで6ラウンドをクリアしよう！');
  }

  private setupRuleButton(): void {
    const ruleButton = document.getElementById('rule-button');
    if (ruleButton) {
      ruleButton.addEventListener('click', () => {
        this.showRuleModal();
      });
    }
  }

  private showRuleModal(): void {
    if (!this.ruleModal) {
      this.ruleModal = new RuleModalScreen();
    }
    this.ruleModal.show();
  }
  
  private showTitleScreen(): void {
    this.currentView = 'title';

    // タイトル画面はCanvasで表示
    this.renderer.setShowTitleScreen(true);
    this.startGameLoop();

    // 一人用モードを直接表示（マルチプレイヤーモードは一時的に無効化）
    setTimeout(() => {
      this.startSinglePlayerGame();
    }, 2000); // 2秒後に自動的にシングルプレイヤーゲームへ
  }
  
  private showModeSelection(): void {
    this.currentView = 'mode-selection';
    
    // Canvasを隠す
    const gameContainer = document.querySelector('.game-container') as HTMLElement;
    if (gameContainer) {
      gameContainer.style.display = 'none';
    }
    
    // モード選択画面を表示
    const modeContainer = document.createElement('div');
    modeContainer.id = 'mode-selection-container';
    document.querySelector('main')?.appendChild(modeContainer);
    
    this.modeSelection = new ModeSelection(
      modeContainer,
      this.authService,
      (mode) => this.handleModeSelect(mode)
    );
  }
  
  private handleModeSelect(mode: 'single' | 'multi'): void {
    this.gameMode = mode;
    
    // モード選択画面を削除
    const modeContainer = document.getElementById('mode-selection-container');
    if (modeContainer) {
      modeContainer.remove();
    }
    
    if (mode === 'single') {
      this.startSinglePlayerGame();
    } else {
      this.showMultiplayerLobby();
    }
  }
  
  private startSinglePlayerGame(): void {
    this.currentView = 'game';
    
    // Canvasを表示
    const gameContainer = document.querySelector('.game-container') as HTMLElement;
    if (gameContainer) {
      gameContainer.style.display = 'block';
    }
    
    // タイトル画面を非表示
    this.renderer.setShowTitleScreen(false);
    
    // シングルプレイヤーモードに設定
    this.renderer.setMultiplayerMode(false);
    
    // インラインランキングを表示
    this.setupInlineRanking();
    
    // ログインボタンを追加（未ログインの場合）
    if (!this.currentUser) {
      this.addLoginButton();
    } else {
      this.handleLoginSuccess(this.currentUser);
    }
    
    // ゲームループ開始
    this.startGameLoop();
  }
  
  private showMultiplayerLobby(): void {
    this.currentView = 'lobby';
    
    // AuthServiceから最新のユーザー状態を取得
    const currentUser = this.authService.getCurrentUser();
    if (!currentUser) {
      alert('マルチプレイヤーに参加するにはログインが必要です');
      this.showLoginScreen();
      return;
    }
    
    // currentUserを更新
    this.currentUser = currentUser;
    
    // ロビー画面を表示
    const lobbyContainer = document.createElement('div');
    lobbyContainer.id = 'multiplayer-lobby-container';
    document.querySelector('main')?.appendChild(lobbyContainer);
    
    this.multiplayerLobby = new MultiplayerLobby(
      lobbyContainer,
      this.authService,
      (roomId) => this.handleRoomJoin(roomId)
    );
  }
  
  private handleRoomJoin(roomId: string): void {
    this.currentView = 'room';
    this.currentRoomId = roomId;
    
    // ロビーを削除
    const lobbyContainer = document.getElementById('multiplayer-lobby-container');
    if (lobbyContainer) {
      lobbyContainer.remove();
    }
    if (this.multiplayerLobby) {
      this.multiplayerLobby.destroy();
      this.multiplayerLobby = null;
    }
    
    // ルーム画面を表示
    const roomContainer = document.createElement('div');
    roomContainer.id = 'multiplayer-room-container';
    document.querySelector('main')?.appendChild(roomContainer);
    
    this.multiplayerRoom = new MultiplayerRoom(
      roomContainer,
      roomId,
      this.authService,
      (boardData) => this.startMultiplayerGame(boardData),
      () => this.handleLeaveRoom()
    );
  }
  
  private startMultiplayerGame(boardData: any): void {
    console.log('[ChimdonMini] Starting multiplayer game with board data:', boardData);
    this.currentView = 'game';
    
    // ルーム画面を削除
    const roomContainer = document.getElementById('multiplayer-room-container');
    if (roomContainer) {
      roomContainer.remove();
    }
    
    // Canvasを表示
    const gameContainer = document.querySelector('.game-container') as HTMLElement;
    if (gameContainer) {
      gameContainer.style.display = 'block';
    }
    
    // タイトル画面を非表示
    this.renderer.setShowTitleScreen(false);
    
    // マルチプレイヤーモードを有効化
    this.renderer.setMultiplayerMode(true);
    
    // ボードデータから盤面を初期化
    if (boardData && boardData.tileSequence && boardData.roundPlacements) {
      console.log('[ChimdonMini] Initializing board with:', {
        tileSequence: boardData.tileSequence.length,
        rounds: Object.keys(boardData.roundPlacements)
      });
      this.board.initFromBoardData(boardData);
    } else {
      console.error('Invalid board data received:', boardData);
      return;
    }
    
    // ミニゲームモードを開始
    this.miniGame.stop();
    this.miniGame.start();
    this.controls.updateUI();
    
    // マルチプレイヤー用のイベントリスナー設定
    this.setupMultiplayerSync();
    
    // 初期スコアボードを設定
    this.updateMultiplayerScores();
    
    // ゲームループ開始
    this.startGameLoop();
  }
  
  private handleLeaveRoom(): void {
    // ルームから退出してロビーに戻る
    if (this.multiplayerRoom) {
      this.multiplayerRoom.destroy();
      this.multiplayerRoom = null;
    }
    
    // ルームのリスナーをクリーンアップ
    if (this.roomUnsubscribe) {
      this.roomUnsubscribe();
      this.roomUnsubscribe = null;
    }
    
    const roomContainer = document.getElementById('multiplayer-room-container');
    if (roomContainer) {
      roomContainer.remove();
    }
    
    // マルチプレイヤーモードを無効化
    this.renderer.setMultiplayerMode(false);
    
    this.currentRoomId = null;
    this.showMultiplayerLobby();
  }
  
  private setupMultiplayerSync(): void {
    if (!this.currentRoomId) return;
    
    // ラウンド完了時の処理を設定
    this.miniGame.setOnRoundComplete((roundNumber) => {
      console.log('Round completed:', roundNumber);
      this.multiplayerService.sendRoundComplete(this.currentRoomId!, roundNumber);
      
      // スコアを更新（仮のスコア計算）
      const score = roundNumber * 10;
      this.multiplayerService.updatePlayerScore(this.currentRoomId!, score);
    });
    
    // ルーム情報を監視してスコアボードとラウンドを更新
    this.roomUnsubscribe = this.multiplayerService.listenToRoom(this.currentRoomId, (room) => {
      if (room && room.status === 'playing') {
        this.updateMultiplayerScores(room);
        
        // ラウンド変更を検知
        const currentRound = this.miniGame.getCurrentRound();
        if (room.currentRound && room.currentRound > currentRound) {
          console.log(`Room advancing to round ${room.currentRound}`);
          this.miniGame.advanceToNextRound();
        }
      }
    });
  }
  
  /**
   * マルチプレイヤーのスコアボードを更新
   */
  private updateMultiplayerScores(room?: any): void {
    if (!this.currentRoomId) return;
    
    // ルーム情報からプレイヤーのスコアを取得
    if (room && room.players) {
      const scores = new Map<string, { name: string, score: number }>();
      
      Object.entries(room.players).forEach(([uid, player]: [string, any]) => {
        scores.set(uid, {
          name: player.displayName || 'Unknown',
          score: player.score || 0
        });
      });
      
      this.renderer.updateMultiplayerScores(scores);
    } else {
      // 初期状態（空のスコアボード）
      this.renderer.updateMultiplayerScores(new Map());
    }
  }
  
  private setupInlineRanking(): void {
    // Canvasの後にランキングを表示するためのdivを作成
    const rankingContainer = document.createElement('div');
    rankingContainer.id = 'inline-ranking-container';
    
    // main要素を取得
    const mainElement = document.querySelector('main');
    if (mainElement) {
      mainElement.appendChild(rankingContainer);
      
      // インラインランキングを作成してマウント
      this.inlineRanking = new InlineRanking();
      this.inlineRanking.mount(rankingContainer);
    }
  }
  
  private addLoginButton(): void {
    // ログインボタンを右上に表示
    const button = document.createElement('button');
    button.className = 'login-prompt-button';
    button.innerHTML = '🔑 ログイン';
    button.title = 'ランキングに参加';
    button.addEventListener('click', () => {
      this.showLoginScreen();
    });
    document.body.appendChild(button);
    
    // ログイン後に削除するためにボタンを保存
    this.loginButton = button;
  }
  
  private loginButton: HTMLElement | null = null;
  
  private showLoginScreen(): void {
    this.loginScreen = new LoginScreen(this.authService, (user) => {
      this.handleLoginSuccess(user);
    }, () => {
      // スキップ処理
      this.loginScreen?.unmount();
      this.loginScreen = null;
      
      // モード選択画面を更新
      if (this.modeSelection) {
        this.modeSelection.updateLoginStatus();
      }
    });
    this.loginScreen.mount(document.body);
  }
  
  private handleLoginSuccess(user: User): void {
    this.currentUser = user;
    
    // Remove login screen if exists
    if (this.loginScreen) {
      this.loginScreen.unmount();
      this.loginScreen = null;
    }
    
    // Remove login button if exists
    if (this.loginButton) {
      this.loginButton.remove();
      this.loginButton = null;
    }
    
    // モード選択画面を更新
    if (this.modeSelection) {
      this.modeSelection.updateLoginStatus();
    }
    
    // ゲーム画面の場合のみプロフィール表示
    if (this.currentView === 'game') {
      // Show user profile display
      this.profileDisplay = new UserProfileDisplay(user, this.authService);
      this.profileDisplay.mount(document.body);
      
      // モーダルランキング表示も初期化（非表示）
      this.rankingDisplay = new RankingDisplay();
      this.rankingDisplay.mount(document.body);
    }
    
    console.log('User logged in:', user.displayName);
  }
  
  
  private async handleGameComplete(totalTime: number, roundNumber: number, boardSeed: string, hintPenalty: number): Promise<void> {
    // マルチプレイヤーモードの場合
    if (this.gameMode === 'multi' && this.currentRoomId) {
      await this.multiplayerService.reportCompletion(this.currentRoomId, totalTime);
      // TODO: マルチプレイヤー結果画面を表示
      return;
    }
    
    // シングルプレイヤーモードの処理
    if (!this.currentUser) {
      console.log('ログインしていないため、結果は記録されません');
      
      // ログインを促すメッセージを表示
      this.showLoginPrompt();
      return;
    }
    
    try {
      // ゲーム結果を保存
      await this.gameResultService.saveGameResult(
        this.currentUser,
        totalTime,
        roundNumber,
        boardSeed,
        hintPenalty,
        0 // greenChipsUsed (Phase 2では常に0)
      );
      
      // ユーザー統計を更新（ローカル）
      this.currentUser.stats.totalGames++;
      if (!this.currentUser.stats.bestTime || totalTime < this.currentUser.stats.bestTime) {
        this.currentUser.stats.bestTime = totalTime;
      }
      
      // プロフィール表示を更新
      if (this.profileDisplay) {
        this.profileDisplay.updateUser(this.currentUser);
      }
      
      console.log('ゲーム結果を保存しました');
      
      // インラインランキングを更新
      if (this.inlineRanking) {
        this.inlineRanking.refresh();
      }
      
    } catch (error) {
      console.error('ゲーム結果の保存に失敗しました:', error);
    }
  }
  
  private showLoginPrompt(): void {
    // ログインを促すトースト表示
    const toast = document.createElement('div');
    toast.className = 'login-prompt-toast';
    toast.innerHTML = `
      <p>🏆 ランキングに参加するにはログインが必要です</p>
      <button id="login-from-toast">ログインする</button>
    `;
    document.body.appendChild(toast);
    
    const loginBtn = toast.querySelector('#login-from-toast');
    if (loginBtn) {
      loginBtn.addEventListener('click', () => {
        toast.remove();
        this.showLoginScreen();
      });
    }
    
    // 5秒後に自動で消える
    setTimeout(() => {
      if (toast.parentNode) {
        toast.remove();
      }
    }, 5000);
  }

  private setupCanvasEvents(): void {
    this.canvas.addEventListener('click', (event) => {
      const rect = this.canvas.getBoundingClientRect();
      // CSSで設定された論理サイズを取得（500x550）
      const x = (event.clientX - rect.left) * 500 / rect.width;
      const y = (event.clientY - rect.top) * 550 / rect.height;

      // ツイートボタンのクリック判定
      if (this.renderer.isTweetButtonClicked(x, y)) {
        this.handleTweetClick();
        return;
      }

      // スタートボタンのクリック判定
      if (this.renderer.isStartButtonClicked(x, y)) {
        this.startNewGame();
        return;
      }

      // 答えを見るボタンのクリック判定
      if (this.renderer.isHintButtonClicked(x, y)) {
        this.showHint();
        return;
      }
      
      const tile = this.renderer.getTileFromCoordinates(x, y, this.board);
      
      if (tile && this.miniGame.isEnabled()) {
        const tileType = tile.getBaseTileType();
        if (tileType !== 'EMPTY') {
          const result = this.miniGame.handleTileClick(tile.coords, x, y);
          if (result) {
            this.controls.updateUI();
            // アニメーションループを開始
            this.startAnimationLoop();
          }
        }
      }
    });

    // ホバー効果
    this.canvas.addEventListener('mousemove', (event) => {
      const rect = this.canvas.getBoundingClientRect();
      // CSSで設定された論理サイズを取得（500x550）
      const x = (event.clientX - rect.left) * 500 / rect.width;
      const y = (event.clientY - rect.top) * 550 / rect.height;
      
      // ツイートボタンのホバー判定
      if (this.renderer.isTweetButtonClicked(x, y)) {
        this.canvas.style.cursor = 'pointer';
        return;
      }

      // スタートボタンのホバー判定（ゲーム中は判定しない）
      if (this.renderer.isStartButtonClicked(x, y)) {
        this.canvas.style.cursor = 'pointer';
        return;
      }

      // 答えを見るボタンのホバー判定
      if (this.renderer.isHintButtonClicked(x, y)) {
        this.canvas.style.cursor = 'pointer';
        return;
      }
      
      const tile = this.renderer.getTileFromCoordinates(x, y, this.board);
      
      if (tile && this.miniGame.isEnabled()) {
        this.canvas.style.cursor = 'pointer';
      } else {
        this.canvas.style.cursor = 'default';
      }
    });
  }

  private startGameLoop(): void {
    // 初回描画
    this.render();
    
    // タイトル画面またはゲーム中は継続的に描画
    const gameLoop = () => {
      if (this.currentView === 'title' || (this.currentView === 'game' && this.miniGame.isEnabled())) {
        this.render();
        requestAnimationFrame(gameLoop);
      }
    };
    
    // ループを開始
    const checkGameStart = () => {
      if (this.currentView === 'title' || (this.currentView === 'game' && this.miniGame.isEnabled())) {
        gameLoop();
      } else {
        setTimeout(checkGameStart, 100);
      }
    };
    checkGameStart();
  }
  
  private animationId: number | null = null;
  
  private startAnimationLoop(): void {
    // アニメーションループが既に動いている場合はスキップ
    if (this.animationId) return;
    
    const animate = () => {
      this.render();
      
      // アニメーションが残っている場合は継続
      const animations = this.miniGame.getActiveAnimations();
      if (animations.length > 0) {
        this.animationId = requestAnimationFrame(animate);
      } else {
        this.animationId = null;
      }
    };
    
    animate();
  }

  public render(): void {
    // ミニゲームモードの情報をレンダラーに設定
    const animations = this.miniGame.getActiveAnimations();
    this.renderer.setAnimations(animations);
    this.renderer.setMiniGameMode(this.miniGame);
    this.renderer.render(this.board);
  }

  public showRouteArrows(route: any[]): void {
    // ルートの矢印を表示
    this.renderer.setRouteArrows(route);
    this.render();
  }

  private startNewGame(): void {
    // 固定のタイル配置で開始（指定された配合、合計26個）
    this.board.regenerate({
      EMPTY: 0,
      BLUE_1: 4,           // 青1
      RED_1: 4,            // 赤1
      BLUE_2: 4,           // 青2
      RED_2: 2,            // 赤2
      BLUE_1_RED_1: 6,     // 青1赤1
      BLUE_2_RED_1: 4,     // 青2赤1
      BLUE_1_RED_2: 2,     // 青1赤2
      BLUE_1_STAR: 0,
      RED_1_STAR: 0,
      BLUE_2_STAR: 0,
      RED_2_STAR: 0,
      BLUE_1_RED_1_STAR: 0,
      BLUE_2_RED_1_STAR: 0,
      BLUE_1_RED_2_STAR: 0
    });
    
    // ミニゲームモードを再スタート
    this.miniGame.stop();
    this.miniGame.start();
    
    // UIを更新
    this.controls.updateUI();
    
    // ゲームループを開始
    this.startGameLoop();
  }
  
  private showHint(): void {
    // ペナルティを加算
    this.miniGame.addHintPenalty();

    // RouteSearcherV4を使ってルートを探索
    const searcher = new RouteSearcherV4(this.board);
    searcher.setMaxGreenChips(0);
    const routes = searcher.findAllRoutes();

    if (routes.length > 0 && routes[0].route) {
      // デバッグ：ルートをシミュレート
      const simResult = this.miniGame.simulateRoute(routes[0].route);
      if (!simResult.success) {
        console.error('⚠️ ROUTE SIMULATION FAILED:', simResult.failReason);
      } else {
        console.log('✅ Route simulation passed');
      }

      // 最初のルートを矢印で表示
      this.renderer.setRouteArrows(routes[0].route);

      // ペナルティアニメーションを開始
      this.renderer.showPenaltyAnimation();

      // アニメーションループを開始（ペナルティ表示用）
      this.startAnimationLoop();

      // 3秒後に矢印を消す
      setTimeout(() => {
        this.renderer.setRouteArrows([]);
        this.render();
      }, 3000);
    } else {
      console.log('ルートが見つかりませんでした');
    }
  }

  private handleTweetClick(): void {
    // ゲーム完了時のデータを取得
    const totalTime = this.miniGame.getTotalElapsedTime();
    const penaltyTime = this.miniGame.getPenaltyTime();
    const roundNumber = this.miniGame.getMaxRounds();

    // Twitterシェアウィンドウを開く
    TwitterShare.openTweetWindow({
      totalTime,
      roundNumber,
      penaltyTime
    });
  }
  
  public destroy(): void {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }
    this.miniGame.stop();
  }
}

// アプリケーション起動
document.addEventListener('DOMContentLoaded', () => {
  try {
    const game = new ChimdonMini();
    // デバッグ用にグローバルに公開
    (window as any).game = game;
    console.log('チムドンミニ started successfully!');
  } catch (error) {
    console.error('Failed to start game:', error);
  }
});